import React from 'react';

import {
    Footer,
    Header,
    Meta
} from '$partials/global';

import { Button } from '$partials/ui';
import { formatLinkBlockToUrl } from '$utilities/format';
import { Media } from '$partials/media';
import { ModuleDispatcher } from '$partials/modules';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').HomePage}
 */
const HomePage = (props) => {
    const page = props.data.page;
    return (
        <>
            <Meta
                description={page.searchDescription || page.listingText || page.standfirst || page.articleStandfirst}
                domain={props.domain}
                image={page.listingImage}
                page={page}
                title={page.__typename === 'Home' ? 'Business Leader' : page.title} />

            <Header
                {...props?.data?.header}
                pageType={page.__typename} />

            <main id='main-content'>
                <div className='layout-container layout-site-outer-padding pb-8 md:pb-16'>
                    <h1 className='sr-only'>
                        {'Business Leader'}
                    </h1>

                    {/* Hero media */}
                    {page.heroMedia && (
                        <section className='col-wrapper-breakout'>
                            <figure>
                                <Media {...{
                                    ...page.heroMedia,
                                    video: {
                                        ...page.heroMedia.video,
                                        autoPlay: true,
                                        loop: true,
                                        showControls: false,
                                    },
                                    image: { ...page.heroMedia?.image, containerClassName:'rounded-lg overflow-hidden' }
                                }}/>
                                {page.heroMediaCaption && (
                                    <figcaption className='mt-8 px-8 md:px-16 type-style-sans-body text-text-default-secondary'>
                                        {page.heroMediaCaption}
                                    </figcaption>
                                )}
                            </figure>
                        </section>
                    )}

                    {/* Hero panel */}
                    {page.panelTitle && (
                        <section className='pt-24 pb-16 mt-16'>
                            <h2 className='type-style-sans-display w-full sm:w-9/12 md:w-10/12'>
                                {page.panelTitle}
                            </h2>
                            {page.panelText && (
                                <p className='type-style-sans-h3 mt-8 w-full sm:w-9/12 md:w-1/2'>
                                    {page.panelText}
                                </p>
                            )}
                            {page.panelButtons?.length > 0 && (
                                <div className='mt-40 flex flex-wrap gap-x-8'>
                                    {page.panelButtons.map((button, idx) => {
                                        return (
                                            <Button
                                                as='a'
                                                className={`${idx === 0 ? 'button-primary-xl' : 'button-outcome-xl'}${button.ctaCssClass ? ' ' + button.ctaCssClass: ''}`}
                                                href={formatLinkBlockToUrl(button.ctaLink)}
                                                key={button.id}>
                                                {button.ctaLabel}
                                            </Button>
                                        );
                                    })}
                                </div>
                            )}
                        </section>
                    )}

                    {page.modules?.length > 0 && (
                        <div className='flex flex-wrap justify-between gap-y-8 md:gap-y-16 mt-8 md:mt-16'>
                            {page.modules.map((module, i) => {
                                let moduleProps;
                                switch (module.__typename) {
                                    case 'MediaBlock':
                                        moduleProps = {
                                            ...module,
                                            isBreakout: true
                                        };
                                        break;

                                    case 'ArticleCarouselBlock':
                                    case 'EventCarouselBlock':
                                        moduleProps = {
                                            ...module,
                                            wrapperClass: 'w-full flex-grow'
                                        };
                                        break;

                                    case 'GenericCardsBlock':
                                    case 'ColumnTextMediaBlock':
                                        moduleProps = {
                                            ...module,
                                            wrapperClass: 'w-full flex-grow px-8 md:px-16 -mx-8 sm:-mx-8 md:-mx-16'
                                        };
                                        break;

                                    default:
                                        moduleProps = module;
                                        break;
                                }

                                return (
                                    <ModuleDispatcher
                                        {...moduleProps }
                                        key={i + module?.id}
                                        prevType={page.modules[i - 1]?.__typename}/>
                                );})}
                        </div>
                    )}
                </div>
            </main>

            <Footer
                {...props?.data?.footer}
                pageType={page.__typename} />
        </>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default HomePage;
